import { useMotionValue, useReducedMotion, useSpring } from "motion/react";
import type { PointerEvent } from "react";

/**
 * Magnetic pointer attraction.
 *
 * The element leans toward the cursor as it approaches and springs back when
 * it leaves. It is a small effect and it does a specific job: it makes a
 * control feel like it is *aware* of you a fraction before you reach it,
 * which reads as responsiveness rather than as decoration.
 *
 * Bounded hard at `strength` pixels. Unbounded magnetism — the version where
 * the element chases the cursor across the page — is a genuine usability bug:
 * the thing you are aiming at moves away from where you aimed, and on a
 * precise pointer it turns a click into two.
 *
 * Off entirely under reduced motion and on touch, where there is no hover
 * state to anticipate and the effect would fire mid-tap.
 */
export function useMagnetic(strength = 6) {
  const reduced = useReducedMotion() ?? false;

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 260, damping: 18, mass: 0.5 });
  const springY = useSpring(y, { stiffness: 260, damping: 18, mass: 0.5 });

  function onPointerMove(event: PointerEvent<HTMLElement>) {
    if (reduced || event.pointerType === "touch") return;
    const box = event.currentTarget.getBoundingClientRect();
    x.set(((event.clientX - box.left) / box.width - 0.5) * strength * 2);
    y.set(((event.clientY - box.top) / box.height - 0.5) * strength * 2);
  }

  function onPointerLeave() {
    x.set(0);
    y.set(0);
  }

  return {
    handlers: { onPointerMove, onPointerLeave },
    style: reduced ? undefined : { x: springX, y: springY },
  };
}
